/**
 * Closing the studio, and finding out who was told (D-735, D-736, D-741).
 *
 * **Its own module rather than part of `calendar.ts`.** The calendar *reads* a
 * closure — whether a day is shut, and from when — and that stays where it is.
 * This is the writing of one, which is a different capability, a different
 * refusal set and a different audience, and the one control in the app that
 * reaches every family at once.
 */

/** What closing these days would call off, and who it would tell. */
export type ClosurePreview = {
  classes: number
  privates: number
  /**
   * Sessions that had already begun and are left running (D-741).
   *
   * What makes *close everything from 6pm* safe to tap at 5:50, and the number
   * that has to be on the screen — otherwise the count of what is called off
   * silently disagrees with what she can see on the grid.
   */
  alreadyStarted: number
  people: number
  emailed: number
  inAppOnly: number
  /** Nobody the studio holds an address or a login for. Counted, never told. */
  unreachable: number
}

/** How far the telling got (D-736). */
export type ClosureProgress = {
  from: string
  to: string
  /** Null until the first sweep has resolved the audience. */
  expected: number | null
  told: number
  notTold: number
  /** Claimed and never stamped: the process died mid-call. Neither told nor failed. */
  notKnown: number
  unreachable: number | null
  finished: boolean
}

export type CloseStudioRequest = {
  from: string
  to?: string | null
  /** Shut from this time onward, or null for the whole of each day. */
  fromTime?: string | null
  reason?: string | null
  /** Why, in words families read. Optional — a snow day explains itself. */
  familyNote?: string | null
  /**
   * Whether these days lose only their regularly scheduled classes (D-868),
   * leaving a class that meets once and a private lesson on.
   *
   * Off unless she says otherwise, and the default is the point: most closures
   * are weather, where everything comes off because nobody is reaching the
   * building. The festival case is the other one — the regular schedule is
   * cancelled and teachers still run privates and file one-off classes that day
   * — and nothing in the data can tell them apart.
   *
   * **It changes the count, so it is part of the preview**, not only of the
   * apply. A number on the button that disagrees with what the button does is
   * D-210 on the control itself.
   *
   * **Fixed once the day is closed.** A second close that disagrees is refused
   * with a 409 naming reopening — either direction would silently move a lesson
   * a family has already been told about.
   */
  regularScheduleOnly?: boolean
}

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init)

  if (!res.ok) {
    // The server writes the sentence - "The studio is already closed for more
    // of 17 November than that", naming reopening - so repeating it here in
    // different words would give one refusal two names.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? `Something went wrong (${res.status})`)
  }

  return res.json()
}

const send = (url: string, body: unknown) =>
  json<ClosurePreview>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

export const previewClosure = (request: CloseStudioRequest) =>
  send('/api/closures/preview', request)

/**
 * What the families will actually read (D-805).
 *
 * **Rendered on the server, from the studio's live template row.** Never
 * composed here: this studio's closure message has been edited, so it does not
 * say what the seeded default says — which is how `{{closureDate}}` reached
 * families and how *"closed on on Wednesday"* reached them the next day. A
 * preview built in the browser would have shown a perfectly good sentence on
 * both occasions, and been read as proof.
 */
export type ClosureMessage = {
  subject: string
  body: string
  /** Placeholders nothing filled. Empty is the ordinary case. */
  unresolved: string[]
  /** False means this message is switched off and nothing will go out. */
  enabled: boolean
}

export const previewClosureMessage = (request: CloseStudioRequest) =>
  json<ClosureMessage>('/api/closures/preview-message', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })

export const closeTheStudio = (request: CloseStudioRequest) =>
  json<{ cancelled: number; alreadyStarted: number; widened: boolean }>('/api/closures', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })

/**
 * How far the telling got for a day, or null where nothing is telling anybody.
 *
 * **404 is an answer, not a failure** (D-209). A closure applied on a host with
 * the sender switched off has no send to report, and rendering that as zeros
 * would read as *nobody has been told yet* — which is a different thing and the
 * one people act on. Null means "there is no send"; a thrown error means "we
 * could not find out", and the screen says each of them differently.
 */
export async function fetchClosureProgress(date: string): Promise<ClosureProgress | null> {
  const res = await fetch(`/api/closures/progress?date=${date}`)

  if (res.status === 404) return null

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? `Something went wrong (${res.status})`)
  }

  return res.json()
}

/** What reopening put back (D-738). */
export type Reopened = {
  from: string
  to: string
  putBack: number
}

/**
 * Puts a closed day back on, as wide as the closure was (D-738).
 *
 * <b>The whole range, never one day of it.</b> A range is one decision and one
 * announcement (D-740), so the undo has to be as wide as the thing it undoes —
 * and reopening per day would stop a send still owed to the other days, leaving
 * them shut with nothing that would ever finish.
 *
 * Refused once the first class it called off would have started, and on a day
 * that has been and gone: by then the evening has happened, so putting it back
 * reaches nobody who can act on it.
 */
export const reopenTheStudio = (date: string) =>
  json<Reopened>(`/api/closures/${date}`, { method: 'DELETE' })
